import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useStyletron } from 'parkdepot-shared/theme'
import { RRule } from 'rrule'

// style components
import { Container, OpeningHoursTitle, OpeningHoursWrapper } from './OpeningHoursSection.style'
import { OpeningHours, OpeningHourContainer } from './OpeningHoursSection.style'
import Icon from '../../../../components/style/Icon.style'

// types
import { OpeningHoursRrules } from 'parkdepot-shared/gql/graphql'
import { allDays, daysMap } from '../../../../utils/getOpeningHours.utils'
import { Duration } from 'luxon'

interface OpeningHoursSectionProps {
    showLabels?: boolean
    openingHours?: OpeningHoursRrules[]
}

export interface OpeningHoursObj {
    duration: string
    rrule: string
}

const OpeningHoursSection: FC<OpeningHoursSectionProps> = ({ showLabels = true, openingHours }) => {
    const { t } = useTranslation()
    const [css, theme] = useStyletron()

    const formatOpeningHours = (
        openingHoursRrules: OpeningHoursObj[]
    ): {
        day: string
        value: string
    }[] => {
        const dayHoursMap: { [key: string]: string } = {}

        openingHoursRrules.forEach((openingHoursRrule) => {
            const { rrule, duration } = openingHoursRrule

            const rruleObj = RRule.fromString(rrule)
            const daysArray = rruleObj.options.byweekday?.map((weekDay) => daysMap[allDays[weekDay]].toUpperCase())
            const hour = rruleObj.options.byhour?.[0]?.toString() || '0'
            const minute = rruleObj.options.byminute?.[0]?.toString() || '0'

            const openingHour = hour ? parseInt(hour, 10) : 0
            const openingMinute = minute ? parseInt(minute, 10) : 0

            const is24Hours = duration === 'P1D' || duration === 'PT0S'

            const durationLuxon = Duration.fromISO(duration)

            const startTime = Duration.fromObject({ hours: openingHour, minutes: openingMinute })
            const endtime = startTime.plus(durationLuxon).normalize()
            const closingHour = endtime.hours
            const closingMinute = endtime.minutes

            const formatTime = (hour: number, minute: number) =>
                `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`

            const openingTime = formatTime(openingHour, openingMinute)
            const closingTime = formatTime(closingHour, closingMinute)

            daysArray.forEach((day) => {
                dayHoursMap[day] = is24Hours ? t('days.openAllDay') : `${openingTime} - ${closingTime}`
            })
        })

        const uniqueTimes = new Set(Object.values(dayHoursMap))

        if (uniqueTimes.size === 1 && uniqueTimes.has(t('days.openAllDay')) && Object.keys(dayHoursMap).length === 7) {
            return [
                {
                    day: `${t('days.weekDays.Mo')} - ${t('days.weekDays.Su')}`,
                    value: t('days.openAllDay')
                }
            ]
        } else if (uniqueTimes.size === 1 && Object.keys(dayHoursMap).length === 7) {
            const commonTime = Object.values(dayHoursMap)[0]
            return [{ day: `${t('days.weekDays.Mo')} - ${t('days.weekDays.Su')}`, value: commonTime }]
        } else {
            return allDays.map((day) => {
                const hours = dayHoursMap[day] || t('days.closed')
                return { day: t(`days.weekDays.${daysMap[day]}`), value: hours }
            })
        }
    }

    const formattedHours = formatOpeningHours(
        openingHours?.map((oh) => ({ duration: oh.duration || '', rrule: oh.rrule || '' })) || []
    )

    return (
        <Container $bottomMobileMargin={showLabels}>
            {showLabels && (
                <div className={css({ marginTop: '5px' })}>
                    <Icon className="pkd-clock" color={theme.colors.primary} aria-hidden="true" />
                </div>
            )}
            <OpeningHourContainer>
                {showLabels && <OpeningHoursTitle>{t('lot.openingHours')}</OpeningHoursTitle>}
                <OpeningHoursWrapper>
                    {formattedHours.map((line, index) => (
                        <OpeningHours key={index}>
                            <dt>{line.day}</dt>
                            <dd>{line.value}</dd>
                        </OpeningHours>
                    ))}
                </OpeningHoursWrapper>
            </OpeningHourContainer>
        </Container>
    )
}

export default React.memo(OpeningHoursSection)
